import { ref } from 'vue'
import { rouletteApi } from '@/api'
import type { SpinRead } from '@/api'
import type { SpinResult } from './game.types'
import { normalizeTonAddress } from '@/utils/ton-address'

export interface SpinSession {
  wallet: string
  id: number
}

export interface PendingSpin {
  wallet: string
  nounce: string
  startedAt: number
}

export type SpinAction = 'new' | 'check' | 'retry'
type StoragePort = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>

const storagePrefix = (wallet: string) => `spotty:pending-spin:v1:${encodeURIComponent(wallet)}:`
const storageKey = (pending: Pick<PendingSpin, 'wallet' | 'nounce'>) =>
  `${storagePrefix(pending.wallet)}${encodeURIComponent(pending.nounce)}`

export class GameService {
  readonly isSpinning = ref(false)
  readonly pendingSpin = ref<PendingSpin | null>(null)
  readonly confirmedSpin = ref<SpinRead | null>(null)
  readonly syncRequired = ref(false)
  readonly storageError = ref(false)
  readonly lastSpinResult = ref<SpinResult | null>(null)
  readonly lastTxHash = ref<string | null>(null)
  private session: SpinSession | null = null

  private readonly storage: () => StoragePort

  constructor(storage: () => StoragePort = () => localStorage) {
    this.storage = storage
  }

  isCurrent(session: SpinSession): boolean {
    return this.session?.id === session.id
  }

  setSession(session: SpinSession | null): void {
    if (this.session?.id === session?.id) return
    this.session = session
    this.isSpinning.value = false
    this.confirmedSpin.value = null
    this.lastSpinResult.value = null
    this.lastTxHash.value = null
    this.pendingSpin.value = null
    this.storageError.value = false
    if (session) this.loadPending(session.wallet)
    this.syncRequired.value = Boolean(this.pendingSpin.value)
  }

  private loadPending(wallet: string): void {
    try {
      const storage = this.storage()
      const records: PendingSpin[] = []
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index)
        if (!key?.startsWith(storagePrefix(wallet))) continue
        const raw = storage.getItem(key)
        if (!raw) continue
        const record: unknown = JSON.parse(raw)
        if (
          !record ||
          typeof record !== 'object' ||
          !('wallet' in record) ||
          record.wallet !== wallet ||
          !('nounce' in record) ||
          typeof record.nounce !== 'string' ||
          !record.nounce ||
          !('startedAt' in record) ||
          typeof record.startedAt !== 'number' ||
          !Number.isFinite(record.startedAt) ||
          storageKey(record as PendingSpin) !== key
        )
          throw new Error('Invalid pending spin')
        records.push(record as PendingSpin)
      }
      // Each nonce owns its record: concurrent tabs cannot overwrite one another.
      records.sort((a, b) => a.startedAt - b.startedAt || a.nounce.localeCompare(b.nounce))
      this.pendingSpin.value = records[0] ?? null
    } catch {
      // Never overwrite an unreadable recovery record with a new operation.
      this.storageError.value = true
    }
  }

  private savePending(pending: PendingSpin): void {
    try {
      this.storage().setItem(storageKey(pending), JSON.stringify(pending))
      this.pendingSpin.value = pending
    } catch {
      this.storageError.value = true
      throw new Error(
        'Unable to save spin recovery. Enable browser storage and reload before spinning.'
      )
    }
  }

  private clearPending(session: SpinSession, nounce: string): void {
    if (!this.isCurrent(session)) return
    try {
      // Only remove this confirmed/rejected operation, then recover any other tab's attempt.
      this.storage().removeItem(storageKey({ wallet: session.wallet, nounce }))
      this.loadPending(session.wallet)
      if (this.storageError.value) throw new Error('Unreadable recovery record')
    } catch {
      this.storageError.value = true
      throw new Error('Unable to update spin recovery. Please reload and check the result.')
    }
  }

  private prepare(action: SpinAction, session: SpinSession): PendingSpin | null {
    if (!this.isCurrent(session)) return null
    this.loadPending(session.wallet)
    if (this.storageError.value)
      throw new Error('Spin recovery storage is unavailable. Please reload before spinning.')
    if (action === 'new' && this.pendingSpin.value)
      throw new Error('Check your previous spin before starting another.')
    if (action !== 'new' && !this.pendingSpin.value) return null
    if (action === 'new') {
      this.confirmedSpin.value = null
      this.savePending({
        wallet: session.wallet,
        nounce: crypto.randomUUID(),
        startedAt: Date.now(),
      })
    }
    const pending = this.pendingSpin.value
    if (this.confirmedSpin.value?.nounce !== pending?.nounce) this.confirmedSpin.value = null
    return pending
  }

  async execute(action: SpinAction, session: SpinSession): Promise<SpinRead | null> {
    if (!this.isCurrent(session) || this.isSpinning.value) return null
    this.isSpinning.value = true
    const pending = this.prepare(action, session)
    if (!pending) return null
    this.syncRequired.value = true
    try {
      const result =
        action === 'check'
          ? await this.findResult(pending, session)
          : await this.submit(pending, session, action === 'new')
      if (!this.isCurrent(session)) return null
      if (result) this.confirmedSpin.value = result
      return result
    } catch (error) {
      if (!this.isCurrent(session)) return null
      throw error
    }
    // The mutation keeps the shared lock through account synchronization.
  }

  private async submit(
    pending: PendingSpin,
    session: SpinSession,
    initial: boolean
  ): Promise<SpinRead | null> {
    const response = await rouletteApi.spin({ nounce: pending.nounce })
    if (!this.isCurrent(session)) return null
    if (response.success && response.data) {
      if (
        response.data.nounce !== pending.nounce ||
        normalizeTonAddress(response.data.user.wallet) !== session.wallet
      ) {
        throw new Error('Spin response could not be verified. Check the result before continuing.')
      }
      return response.data
    }
    if (response.errorCode === 'duplicate_nounce') return this.findResult(pending, session)
    // Only documented, definite rejections release the operation. Transport errors and
    // unexpected server responses may have happened after the spin was committed.
    // A rejection of a retry says nothing about the outcome of the original request.
    if (initial && [401, 403, 422].includes(response.status ?? 0))
      this.clearPending(session, pending.nounce)
    throw new Error(
      response.error ?? 'The spin result is not confirmed. Check the result before continuing.'
    )
  }

  private async findResult(pending: PendingSpin, session: SpinSession): Promise<SpinRead | null> {
    const limit = 100
    // Bound each read-only recovery attempt. Absence is never treated as proof of failure.
    for (let offset = 0; offset < 2000; offset += limit) {
      const response = await rouletteApi.getUserSpins(limit, offset)
      if (!this.isCurrent(session)) return null
      if (!response.success || !response.data)
        throw new Error(response.error ?? 'Unable to check the spin result.')
      const found = response.data.items.find(
        (spin) =>
          spin.nounce === pending.nounce && normalizeTonAddress(spin.user.wallet) === session.wallet
      )
      if (found) return found
      if (!response.data.items.length || offset + limit >= response.data.total) return null
    }
    return null
  }

  completeSync(session: SpinSession): void {
    if (!this.isCurrent(session)) return
    const confirmed = this.confirmedSpin.value
    if (confirmed) this.clearPending(session, confirmed.nounce)
    if (!this.isCurrent(session)) return
    if (!this.pendingSpin.value) this.syncRequired.value = false
  }

  finish(session: SpinSession): void {
    if (this.isCurrent(session)) this.isSpinning.value = false
  }

  reveal(result: SpinRead, session: SpinSession): boolean {
    if (!this.isCurrent(session) || normalizeTonAddress(result.user.wallet) !== session.wallet)
      return false
    this.lastSpinResult.value = result.won
      ? { type: 'win', amount: result.prizes[0]?.amount ?? 0 }
      : { type: 'miss' }
    this.lastTxHash.value = result.prizes[0]?.explorer_link || null
    return true
  }
}

export const gameService = new GameService()
